<script setup>
import { ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import AppLayout from '@/Layouts/AppLayout.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import SecondaryButton from '@/Components/SecondaryButton.vue'
import TextInput from '@/Components/TextInput.vue'
import SelectInput from '@/Components/SelectInput.vue'
import PaginationLinks from '@/Components/PaginationLinks.vue'
import ExportLoadingOverlay from '@/Components/ExportLoadingOverlay.vue'

const props = defineProps({
  logs: Object,
  employees: Array,
  selectedEmployee: Number,
  lastSyncedAt: String,
  selectedDescription: String,
  selectedModule: String,
})

console.log('Props:', props);

const selectedEmployee = ref(props.selectedEmployee || '')
const descriptionFilter = ref(props.selectedDescription || '')
const moduleFilter = ref(props.selectedModule || '')
const deleteDate = ref('')

const submitEmployeeFilter = () => {
  const params = {}

  if (selectedEmployee.value) params.eid = selectedEmployee.value
  if (descriptionFilter.value) params.desc = descriptionFilter.value
  if (moduleFilter.value) params.module = moduleFilter.value

  Inertia.get(route('admin.audit-log.index'), params, {
    preserveScroll: true,
    preserveState: true,
  })
}

const clearFilter = () => {
  selectedEmployee.value = ''
  descriptionFilter.value = ''
  moduleFilter.value = ''

  Inertia.get(route('admin.audit-log.index'), {}, {
    preserveScroll: true,
    preserveState: true,
  })
}

const exporting = ref(false)

const onExportSubmit = () => {
  exporting.value = true
}
</script>

<template>
  <AppLayout title="Track Log List">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Track Log List
      </h2>
    </template>

    <ExportLoadingOverlay v-model="exporting" title="Exporting…" message="Generating file, please wait. File may take a few moments to download. Click to dismiss."
      :autoCloseOnFocus="true" :closeOnUserInteraction="true" :interactionArmDelayMs="1000" />

    <div class="mx-auto py-8 sm:px-6 lg:px-8 space-y-6">
      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center gap-4">
          <form :action="route('admin.audit-log.sync')" method="post" class="inline-flex items-center gap-2">
            <input type="hidden" name="_token" :value="$page.props.csrf_token" />
            <PrimaryButton type="submit">
              Sync New Activity Log
            </PrimaryButton>
          </form>

          <span v-if="lastSyncedAt">
            Last record found for sync:
            <strong>{{ lastSyncedAt }}</strong>
          </span>
        </div>

        <div class="flex flex-wrap items-center gap-4">
          <form :action="route('admin.audit-log.export')" method="post" class="inline-flex items-center gap-2"
            @submit="onExportSubmit">
            <input type="hidden" name="_token" :value="$page.props.csrf_token" />
            <input type="hidden" name="user_id" :value="selectedEmployee || ''" />
            <input type="hidden" name="module" :value="moduleFilter || ''" />
            <input type="hidden" name="desc" :value="descriptionFilter || ''" />
            <PrimaryButton type="submit">
              Export
            </PrimaryButton>
          </form>
        </div>
      </div>

      <div class="p-4 rounded-md bg-white shadow-md">
        <div>
          <h3 class="font-semibold mb-3">Search Projects</h3>

          <form @submit.prevent="submitEmployeeFilter" class="flex flex-wrap items-end gap-4">
            <div class="flex flex-col">
              <label class="text-sm font-medium mb-1">
                Employee Name:
              </label>
              <SelectInput v-model="selectedEmployee">
                <option value="">-----Select------</option>
                <option v-for="emp in employees" :key="emp.employeeID" :value="emp.employeeID">
                  {{ emp.firstname }} {{ emp.lastname }}
                </option>
              </SelectInput>
            </div>

            <div class="flex flex-col">
              <label class="text-sm font-medium mb-1">Module:</label>
              <TextInput v-model="moduleFilter" placeholder="e.g. Project, Inventory..." />
            </div>

            <div class="flex flex-col">
              <label class="text-sm font-medium mb-1">Description:</label>
              <TextInput v-model="descriptionFilter" placeholder="Search log description..." />
            </div>

            <PrimaryButton type="submit">
              Search
            </PrimaryButton>

            <SecondaryButton type="button" @click="clearFilter">
              Clear
            </SecondaryButton>

          </form>
        </div>

        <!--<div>
          <h3 class="font-semibold mb-3">Delete Audit Till</h3>
          <form :action="route('admin.audit-log.delete')" method="post" class="flex flex-wrap items-center gap-2">
            <input type="hidden" name="_token" :value="$page.props.csrf_token" />
            <input type="hidden" name="eid" :value="selectedEmployee || ''" />
            <label class="text-sm font-medium min-w-[120px]">
              Delete Audit Till:
            </label>
            <input
              v-model="deleteDate"
              type="date"
              name="delete_date"
              class="border-gray-300 rounded-md shadow-sm mt-1"
              required
            />
            <PrimaryButton type="submit">
              Delete
            </PrimaryButton>
          </form>
        </div>-->
      </div>

      <div class="bg-white shadow-sm rounded-md p-4">
        <table class="min-w-full w-full text-left text-sm">
          <thead class="border-b">
            <tr>
              <th class="px-4 py-2">Employee Name</th>
              <th class="px-4 py-2">Module</th>
              <th class="px-4 py-2">Log Date/Time</th>
              <th class="px-4 py-2">Log Description</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!logs.data.length">
              <td colspan="4" class="px-4 py-4 text-center text-gray-500">
                No Records found
              </td>
            </tr>

            <tr v-for="log in logs.data" :key="log.id" class="border-b">
              <td class="px-4 py-2">
                {{ log.employee_name || '—' }}
              </td>
              <td class="px-4 py-2">
                <template v-if="log.module_url">
                  {{ log.module_label }}
                </template>
                <template v-else>
                  {{ log.module_label }}
                </template>
              </td>
              <td class="px-4 py-2">
                {{ log.log_date_formatted || '—' }}
              </td>
              <td class="px-4 py-2">
                {{ log.log_desc }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="mt-4">
          <PaginationLinks :pagination="logs" />
        </div>
      </div>
    </div>
  </AppLayout>
</template>
